import { useState } from 'react';
import { css } from '@emotion/react';
import { useNavigate } from '@tanstack/react-location';
import { BillingConstant } from '@frontend/api';
import { authnClient } from '@frontend/auth';
import {
  LOGIN_ERROR_CAUSE,
  clearLastVisitedPage,
  clearLocalStorageKeys,
  getLastVisitedPage,
} from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  ContentLoader,
  EmailField,
  FormRow,
  Modal,
  PasswordField,
  PrimaryButton,
  TextLink,
  useForm,
  useModalControl,
  useAlert,
} from '@frontend/design-system';
import { PasswordReset } from './password-reset';

export const LegacySignIn = () => {
  const alerts = useAlert();
  const navigate = useNavigate();
  const { modalProps, triggerProps } = useModalControl();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { t } = useTranslation('SignIn');

  const submitSignIn = (user: { username: string; password: string }) => {
    authnClient
      .signIn({ username: user.username, password: user.password })
      .then(() => {
        const lastVisitedPage = getLastVisitedPage();
        clearLastVisitedPage();
        clearLocalStorageKeys([BillingConstant.BillingAlertLocalStorageKeys.billingAlertModalAcknowledged]);
        navigate({ to: !!lastVisitedPage ? lastVisitedPage : '/portal' });
        reset();
      })
      .catch((err: Error) => {
        if (err.cause === LOGIN_ERROR_CAUSE.credentials) {
          alerts.error(t('Incorrect credentials. Please try again'));
        } else if (err.cause === LOGIN_ERROR_CAUSE.unknown) {
          alerts.error(t('Login failed. Please try again'));
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const { formProps, getFieldProps, isComplete, reset } = useForm({
    fields: {
      username: { type: 'email', required: true },
      password: { type: 'password', required: true, minChars: 1 },
    },
    onSubmit: (values) => {
      setIsLoading(true);
      const user = {
        username: values.username as string,
        password: values.password as string,
      };
      submitSignIn(user);
    },
  });

  return (
    <>
      <form {...formProps}>
        <ContentLoader message={t('Signing in')} show={isLoading} />

        <FormRow
          css={css`
            text-align: initial;
          `}
        >
          <EmailField {...getFieldProps('username')} label='Email' />
        </FormRow>

        <FormRow
          css={css`
            text-align: initial;
          `}
        >
          <PasswordField {...getFieldProps('password')} label='Password' />
        </FormRow>

        <PrimaryButton
          disabled={!isComplete}
          type='submit'
          css={css`
            margin-bottom: ${theme.spacing(1)};
            width: 100%;
          `}
        >
          {t('Sign In')}
        </PrimaryButton>
      </form>

      <TextLink
        css={css`
          margin-top: ${theme.spacing(2)};
          margin-bottom: ${theme.spacing(1)};
          font-size: ${theme.fontSize(12)};
          cursor: pointer;
          text-align: center;
          width: 100%;
        `}
        onClick={triggerProps.onClick}
      >
        {t('Forgot your password?')}
      </TextLink>

      <Modal {...modalProps}>
        <PasswordReset modalProps={modalProps} />
      </Modal>
    </>
  );
};
